// MarginNote — always-on note-taking panel for the Library reader.
// Floating button sits at the bottom-right of the reader; when opened,
// a side sheet shows: the page the user was on when each note was made,
// autosave, and editable history. Notes are scoped per (slug, page).
//
// Backend writes go to /api/library/field-notes/{slug} with a synthetic
// quartile key derived from current page so it threads into the same
// storage already supported by library_routes.py.

import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Notebook, X, Plus, Save, Trash2, Check } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = (slug) => `mm_margin_notes_${slug}`;

export default function MarginNote({ slug, currentPage, pageCount, isLightMode }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState([]);   // [{id, page, text, savedAt}]
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const draftRef = useRef(null);

  // Restore notes from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY(slug));
      if (raw) setNotes(JSON.parse(raw));
    } catch { /* noop */ }
  }, [slug]);

  // Persist locally on every change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY(slug), JSON.stringify(notes));
    } catch { /* noop */ }
  }, [notes, slug]);

  const addNote = async () => {
    const text = draft.trim();
    if (!text) return;
    setSaving(true);
    const n = { id: `${Date.now()}`, page: currentPage, text, savedAt: new Date().toISOString() };
    setNotes((prev) => [n, ...prev]);
    setDraft("");
    // Mirror to backend (best-effort)
    try {
      const quartile = pageCount ? Math.round((currentPage / pageCount) * 100) / 100 : 0.0;
      await axios.post(
        `${API}/api/library/field-notes/${slug}`,
        {
          quartile,
          prompt: `Page ${currentPage} note`,
          note: text,
        },
        { withCredentials: true }
      );
    } catch { /* silent — already saved locally */ }
    setSaving(false);
    toast.success("Note saved to your shelf");
  };

  const deleteNote = (id) => {
    setNotes((prev) => prev.filter((n) => n.id !== id));
  };

  const onKey = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      addNote();
    }
  };

  const noteCount = notes.length;

  // Theme tokens
  const panelBg = isLightMode ? "bg-white text-ink" : "bg-[#141416] text-cream";
  const ring = isLightMode ? "border-ink/15" : "border-cream/15";
  const muted = isLightMode ? "text-ink/55" : "text-cream/55";
  const subtle = isLightMode ? "bg-ink/5" : "bg-cream/5";

  return (
    <>
      {/* Floating trigger — always reachable */}
      <button
        onClick={() => { setOpen(true); setTimeout(() => draftRef.current?.focus(), 50); }}
        data-testid="margin-note-trigger"
        className={`fixed right-3 bottom-4 sm:bottom-5 z-30 flex items-center gap-2 px-4 py-3 rounded-full shadow-xl border ${isLightMode ? "bg-ink text-cream border-ink/20" : "bg-cream text-ink border-cream/20"} hover:scale-[1.02] transition-transform`}
        title="Take a note on this page"
        aria-label="Open notes"
      >
        <Notebook className="h-4 w-4" />
        <span className="overline text-[10px] tracking-[0.22em] hidden sm:inline">
          Notes
        </span>
        {noteCount > 0 && (
          <span className={`overline text-[9px] tracking-[0.18em] px-1.5 py-0.5 rounded-full ${isLightMode ? "bg-cream/20 text-cream" : "bg-ink/15 text-ink"}`}>
            {noteCount}
          </span>
        )}
      </button>

      {/* Sheet */}
      {open && (
        <div className="fixed inset-0 z-[56] flex" data-testid="margin-note-sheet">
          <button
            className="flex-1 bg-ink/60 backdrop-blur-sm"
            onClick={() => setOpen(false)}
            aria-label="Close notes"
            data-testid="margin-note-backdrop"
          />
          <aside className={`${panelBg} w-full max-w-md flex flex-col shadow-2xl border-l ${ring}`}>
            {/* Header */}
            <div className={`px-5 py-4 border-b ${ring} flex items-start justify-between`}>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Notebook className="h-4 w-4 opacity-80" />
                  <span className="overline text-[10px] tracking-[0.3em] opacity-70">Margin notes</span>
                </div>
                <h3 className="font-serif italic text-2xl leading-none">
                  Catch the line you{"'"}d screenshot.
                </h3>
                <p className={`text-[12px] mt-2 ${muted}`}>
                  Notes auto-save to your shelf. Cmd / Ctrl + Enter to save.
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                className={`p-2 ${muted} hover:opacity-100 opacity-70`}
                aria-label="Close"
                data-testid="margin-note-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Composer */}
            <div className={`p-5 border-b ${ring}`}>
              <div className={`flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase ${muted} mb-2`}>
                <span>P.{currentPage}{pageCount ? ` / ${pageCount}` : ""}</span>
                <span className="opacity-50">·</span>
                <span>New note</span>
              </div>
              <textarea
                ref={draftRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKey}
                placeholder="Quote the line, jot the question, sketch the move…"
                rows={4}
                data-testid="margin-note-textarea"
                className={`w-full bg-transparent border ${ring} focus:outline-none focus:border-current/60 p-3 text-[14px] leading-relaxed font-serif resize-none`}
              />
              <div className="mt-2 flex items-center justify-between">
                <span className={`text-[10px] ${muted}`}>{draft.length} chars</span>
                <button
                  onClick={addNote}
                  disabled={saving || !draft.trim()}
                  data-testid="margin-note-save"
                  className={`inline-flex items-center gap-2 overline text-[10px] tracking-[0.28em] px-4 py-2 ${isLightMode ? "bg-ink text-cream" : "bg-cream text-ink"} disabled:opacity-40 hover:opacity-90 transition-opacity`}
                >
                  {saving ? <Save className="h-3 w-3 animate-pulse" /> : <Plus className="h-3 w-3" />}
                  {saving ? "Saving…" : "Save note"}
                </button>
              </div>
            </div>

            {/* Saved notes */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              <div className={`overline text-[10px] tracking-[0.28em] ${muted}`}>
                Your shelf{noteCount > 0 ? ` · ${noteCount} ${noteCount === 1 ? "note" : "notes"}` : ""}
              </div>

              {noteCount === 0 ? (
                <div className={`text-center py-12 ${muted}`}>
                  <p className="font-serif italic text-base mb-2">No notes yet on this edition.</p>
                  <p className="text-[11px] tracking-wide opacity-80">The first line you catch lives here forever.</p>
                </div>
              ) : (
                notes.map((n) => (
                  <article
                    key={n.id}
                    className={`${subtle} border ${ring} p-4`}
                    data-testid={`margin-note-${n.id}`}
                  >
                    <div className={`flex items-center justify-between text-[10px] tracking-[0.28em] uppercase ${muted} mb-2`}>
                      <span>p.{n.page}</span>
                      <button
                        onClick={() => deleteNote(n.id)}
                        className="opacity-40 hover:opacity-90 transition-opacity"
                        aria-label="Delete note"
                        data-testid={`margin-note-delete-${n.id}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                    <p className="font-serif text-[14px] leading-relaxed whitespace-pre-wrap">{n.text}</p>
                    <p className={`mt-2 text-[10px] tracking-wide ${muted}`}>
                      <Check className="inline h-3 w-3 mr-1" />
                      Saved {timeAgo(n.savedAt)}
                    </p>
                  </article>
                ))
              )}
            </div>
          </aside>
        </div>
      )}
    </>
  );
}

function timeAgo(iso) {
  try {
    const t = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - t);
    const mins = Math.round(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.round(hrs / 24);
    return `${days}d ago`;
  } catch {
    return "moments ago";
  }
}
